// shared/components/conversation/ChatMessageContent.mjs
// Lightweight markdown-link renderer for chat messages.
// Parses [text](url) patterns and renders them as styled, clickable links.

import React, { useMemo } from 'react';

/**
 * Regex that matches markdown links: [link text](url)
 * Also consumes optional surrounding parentheses from OpenAI citation
 * format like ([domain.com](url)) so they don't render as stray text.
 * Captures: group 1 = link text, group 2 = url
 */
const MD_LINK_RE = /\(?\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)\)?/g;

/**
 * Extract the hostname from a URL for display as a domain badge.
 */
const getDomain = (url) => {
    try {
        return new URL(url).hostname.replace(/^www\./, '');
    } catch {
        return url;
    }
};

/**
 * Build a Google favicon URL for a given link URL.
 */
const getFaviconUrl = (url) => {
    const domain = getDomain(url);
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=16`;
};

/**
 * Parse message content string into an array of { type, value } segments.
 *   type: 'text'  → plain text
 *   type: 'link'  → { text, url, domain }
 */
const parseContent = (content) => {
    if (!content) return [];

    const segments = [];
    let lastIndex = 0;

    // Reset regex state
    MD_LINK_RE.lastIndex = 0;

    let match;
    while ((match = MD_LINK_RE.exec(content)) !== null) {
        // Push any text before this match
        if (match.index > lastIndex) {
            segments.push({ type: 'text', value: content.slice(lastIndex, match.index) });
        }

        const [, linkText, url] = match;
        segments.push({
            type: 'link',
            text: linkText,
            url,
            domain: getDomain(url),
        });

        lastIndex = MD_LINK_RE.lastIndex;
    }

    // Remaining text after last match
    if (lastIndex < content.length) {
        segments.push({ type: 'text', value: content.slice(lastIndex) });
    }

    return segments;
};

/**
 * ChatMessageContent — renders assistant message text with inline
 * citation links styled as clickable pills with favicons.
 *
 * @param {{ content: string }} props
 */
const ChatMessageContent = ({ content }) => {
    const segments = useMemo(() => parseContent(content), [content]);

    if (segments.length === 0) return null;

    // Fast path: no links found → render as plain text (avoids extra DOM)
    if (segments.length === 1 && segments[0].type === 'text') {
        return <>{segments[0].value}</>;
    }

    return (
        <>
            {segments.map((seg, i) => {
                if (seg.type === 'text') {
                    return <span key={i}>{seg.value}</span>;
                }

                return (
                    <a
                        key={i}
                        className="chat-panel__source-link"
                        href={seg.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={seg.url}
                    >
                        <img
                            className="chat-panel__source-favicon"
                            src={getFaviconUrl(seg.url)}
                            alt=""
                            width={14}
                            height={14}
                            loading="lazy"
                        />
                        <span className="chat-panel__source-text">{seg.text}</span>
                        <svg className="chat-panel__source-external" viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M4.5 1.5H2a.5.5 0 00-.5.5v8a.5.5 0 00.5.5h8a.5.5 0 00.5-.5V7.5M7 1.5h3.5V5M6 6l4.5-4.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </a>
                );
            })}
        </>
    );
};

export default ChatMessageContent;
